<template>
  <div class="simple-scatterplot">
    <svg ref="svg" :width="width + margin.left + margin.right" :height="height + margin.top + margin.bottom">
      <g ref="g" :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }">
      </g>
    </svg>
  </div>
</template>

<script>
import { select, mouse, selectAll } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { transition } from 'd3-transition'
import { map, assignIn, get, find, filter, maxBy, has, forEach } from 'lodash';
import { parse } from 'date-fns';
import { resizable } from 'views/mixins'
import { mapState } from 'vuex'

export default {

  mixins: [resizable],

  props: {
    data: {
      type: Array,
      required: true
    },
    config: {
      type: Object,
      required: true
    }
  },
  
  computed: {
    ...mapState('audience', [
      'usersWithContextUpdate'
    ])
  },

  data() {
    return {
      width: 0,
      height: 0,
      margin: { top: 6.5, right: 6.5, bottom: 6.5, left: 6.5 },
      x: scaleLinear(),
      y: scaleLinear(),
    }
  },

  mounted() {
    this.setBounds()
    this.x
      .range([0, this.width])
        .domain([0, 1]);
    this.y
      .range([this.height, 0])
      .domain([-90, 90]);
    this.update(this.data)
  },

  methods: {
    onResize() {
      this.setBounds()
      this.x.range([0, this.width]);
      this.y.range([this.height, 0]);

      select(this.$refs.g).selectAll('circle')
        .attr('cx', d => this.getX(d))
        .attr('cy', d => this.getY(d))
    },

    setBounds () {
      const rect = this.$el.getBoundingClientRect()

      this.width = rect.width - this.margin.left - this.margin.right;
      this.height = rect.height - this.margin.top - this.margin.bottom;
    },

    update(data) {
      let maxDuration = 0

      data = map(data, d => {
        const item = this.getItem(d)

        if (!item) {
          return
        }

        const duration = this.getDuration(item)

        if (duration > maxDuration) {
          maxDuration = duration
        }

        return { ...d, duration }
      })

      data = filter(data)

      this.x.domain([0, maxDuration])
      
      const circle = select(this.$refs.g).selectAll('circle')
        .data(data, d => d.id)

      circle.exit()
        .transition()
        .duration(400)
        .attr('cx', d => {
          const max = this.x.domain()[1]

          return this.x(max)
        })
        .style('opacity', 0)
        .remove()

      circle.transition()
        .duration(200)
        .attr('cx', d => this.getX(d))
        .attr('cy', d => this.getY(d))

      circle.enter().append('circle')
        .on('mouseover', data => {
          const node = select(this.$refs.svg).select(`#node-${data.id}`)

          node.attr('r', 7)
            .raise()

          this.emitEvent('node-hover', { data, node })
        })
        .on('click', data => this.emitEvent('node-click', { data, node: null }))
        .on('mouseout', d => this.clearHover(d))
        .attr('class', 'circle')
        .attr('data-event-type', d => d._presentEventType.toLowerCase())
        .attr('id', d => `node-${d.id}`)
        .attr('cx', d => 0)
        .attr('cy', d => this.getY(d))
        .attr('r', 3)
        .style('opacity', 0)
      .transition()
        .duration(200)
        .attr('cx', d => this.getX(d))
        .style('opacity', 1)

      select(this.$refs.svg)
        .selectAll('.is-hovered')
        .each(function (d) {
          select(this).raise()
        })
    },

    clearHover(d) {
      this.$emit('node-hover', null, null)
      select(`#node-${d.id}`)
        .attr('r', 3)
        .classed('is-hovered', false)
    },

    /**
     * Emits event name, data and screen position of node
     *
     * @param name
     * @param data
     */
    emitEvent(name, payload) {
      const { data, node } = payload
      let pos

      if (node) {
        const cx = parseFloat(node.attr('cx'))
        const cy = parseFloat(node.attr('cy'))
        const rect = this.$el.getBoundingClientRect()
        
        pos = {
          x: cx + rect.x + 7,
          y: cy + rect.y + 7
        }
      }

      this.$emit(name, data, pos)
    },

    highlightNode(id) {
      select(this.$refs.svg)
        .selectAll('.circle')
        .each(function (d) {
          select(this)
            .classed('is-hovered', false)
            .attr('r', 3)
        })

      this.$nextTick(() => {
        select(this.$refs.svg)
          .selectAll(`#node-${id}`)
          .each(function (d) {
            select(this)
              .attr('r', 7)
              .classed('is-hovered', true)
              .raise()
          })
      })
    },

    getX(user) {
      const duration = get(user, 'duration')

      return this.x(duration)
    },

    getY(user) {
      const latitude = get(user, 'location.latitude')

      return this.y(latitude)
    },

    getDuration(item) {
      const start = item.start_ts
      const end = item.end_ts || new Date()

      const duration = end - start
      
      return duration
    },

    getItem(user) {
      let item = {}

      if (this.config.category === 'present') {
        item = this.getPresentItem(user)
      } else if (this.config.category === 'past') {
        item = this.getPastItem(user)
      } else if (this.config.category === 'future') {
        item = this.getFutureItem(user)
      }

      return item
    },

    getPresentItem(user) {
      let items = this.config.key === 'moments' ? user.moment_history : user.event_history

      const item = find(items, d => {
        let path;

        if (this.config.key === 'moments') {
          path = 'moment_definition_id'
        } else {
          path = d.type === 'Transport' ? 'mode' : 'location.significance'
        }

        return d.end_ts === null && get(d, path) === this.config.value
      });

      return item
    },

    getPastItem(user) {
      let items = this.config.key === 'moments' ? user.moment_history : user.event_history
      const lastItem = maxBy(items, 'end_ts')

      const item = find(items, d => {
        let path;

        if (this.config.key === 'moments') {
          path = 'moment_definition_id'
        } else {
          path = d.type === 'Transport' ? 'mode' : 'location.significance'
        }

        return d.end_ts === lastItem.end_ts && get(d, path) === this.config.value
      });

      return item
    },

    getFutureItem(user) {
      const item = find(user.prediction_tree.events, d => {
        const path = d.type === 'TransportPrediction' ? 'mode' : 'location_type'

        return get(d, path) === this.config.value
      })

      return item
    }
  },

  watch: {
    data: function (newData) {
      this.update(newData)
    },

    usersWithContextUpdate: function (newUsers) {
      forEach(newUsers, user => {
        const el = select(`#node-${user.id}`)

        if (this.config.category !== 'present' || el.empty()) {
          return
        }

        setTimeout(() => {
          el.raise()
        }, user._updateDelay)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
@import '~scss/utilities/mixins.scss';

.moment-popup-wrapper {
  position: absolute;
  margin-left: 1.125rem;
  margin-top: .375rem;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 99999;
  @include diffused-drop-shadow(0 2px 40px 0 rgba(0, 0, 0, 0.24));
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.simple-scatterplot {
  position: relative;

  svg {
    position: absolute;
    left: 0;
    top: 0;
  }

  .circle {
    // stroke: #9b9b9b;
    stroke: $white;
    stroke-width: 1px;
    fill: $white;
    transition: $transition;
    cursor: pointer;

    &.is-hovered {
      // stroke: #447bb9;
      stroke-width: 1.5px;
    }

    &[data-event-type="transport"] {
      fill: #4a90e2;
    }

    &[data-event-type="stationary"] {
      fill: #e57373;
    }
  }
}
</style>
